/**
 * File Upload Constants
 * Centralized constants for file upload functionality across the application
 */

import { KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES } from '@inference.engine/file-utils/web';

import { i18n } from '@/shared/i18n';

/**
 * Flat list of accepted file extensions for knowledge base uploads (e.g. ['.pdf', '.docx', …]).
 * Use this for validating uploaded files by extension — more robust than MIME-based checks
 * because browsers set `file.type` to '' or 'application/octet-stream' for extensions the OS
 * doesn't recognize (e.g. `.campaign`), even when the file is valid content the backend accepts.
 */
export const KNOWLEDGE_BASE_ALLOWED_EXTENSIONS = Array.from(
  new Set(
    Object.values(KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES)
      .flat()
      .map((ext) => ext.toLowerCase()),
  ),
);

export const KNOWLEDGE_BASE_SHOWCASED_FORMATS = Array.from(
  new Set(
    Object.values(KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES)
      .flat()
      .map((ext) => ext.replace('.', '').toUpperCase()),
  ),
).sort();

export const FILE_VALIDATION_MESSAGES = {
  SIZE_EXCEEDED: (fileName: string, maxSizeMB: number) =>
    i18n.t('common:constants.fileUpload.sizeExceeded', { fileName, maxSizeMb: maxSizeMB }),
  UNSUPPORTED_TYPE: (fileName: string) =>
    i18n.t('common:constants.fileUpload.unsupportedType', { fileName }),
  get UPLOAD_FAILED() {
    return i18n.t('common:constants.fileUpload.failedToUploadFilesPlease');
  },
} as const;

export const UPLOAD_RESPONSE_PATTERNS = {
  PROCESSED: /(\d+) files processed and added to knowledge bases/,
  FAILED: /(\d+) files failed to process/,
  SKIPPED: /(\d+) files skipped \(duplicates\)/,
} as const;

// Functions and a getter rather than plain strings: these are read at module
// scope by consumers, so a resolved value here would freeze English at import.
export const UPLOAD_RESPONSE_MESSAGES = {
  PROCESSED: (filename: string) =>
    i18n.t('common:constants.fileUpload.fileProcessedAndAdded', { filename }),
  FAILED: (filename: string) =>
    i18n.t('common:constants.fileUpload.fileFailedToProcess', { filename }),
  SKIPPED: (filename: string) =>
    i18n.t('common:constants.fileUpload.fileSkippedDuplicate', { filename }),
  get NO_FILES_PROCESSED() {
    return i18n.t('common:constants.fileUpload.noFilesWereProcessed');
  },
} as const;
